// frontend/src/components/formularios/campos/TablaPlanTerapeutico.jsx
import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Button,
  Box,
  Typography,
  Tooltip,
  Chip,
  Collapse,
  Stack,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  FormatListNumbered as NumberIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  SubdirectoryArrowRight as SubArrowIcon
} from '@mui/icons-material';
import CampoModal from './CampoModal';

// Funciones helper (incluidas directamente por si no existe el archivo de utilidades)
const prepararDatosParaGuardar = (areas) => {
  return areas.map(area => ({
    ...area,
    objetivos_especificos: Array.isArray(area.objetivos_especificos) 
      ? area.objetivos_especificos
      : []
  }));
};

const prepararDatosParaCargar = (value) => {
  if (!value || !Array.isArray(value)) {
    return [{
      numero: 1,
      area: '',
      objetivos_generales: '',
      objetivos_especificos: [{
        id: `obj_${Date.now()}_1`,
        texto: '',
        estrategias_actividades: '',
        metas_medicion: ''
      }]
    }];
  }
  
  return value.map((area, index) => ({
    numero: area.numero || index + 1,
    area: area.area || area.area_intervenir || '',
    objetivos_generales: area.objetivos_generales || '',
    objetivos_especificos: normalizarObjetivosEspecificos(area.objetivos_especificos)
  }));
};

const normalizarObjetivosEspecificos = (objetivos) => {
  if (!objetivos) {
    return [{
      id: `obj_${Date.now()}_1`,
      texto: '',
      estrategias_actividades: '',
      metas_medicion: ''
    }];
  }
  
  if (typeof objetivos === 'string') {
    const lineas = objetivos.split('\n').filter(l => l.trim());
    if (lineas.length === 0) {
      return [{
        id: `obj_${Date.now()}_1`,
        texto: '',
        estrategias_actividades: '',
        metas_medicion: ''
      }];
    }
    
    return lineas.map((texto, idx) => ({
      id: `obj_migrado_${Date.now()}_${idx}`,
      texto: texto.replace(/^\d+[\.\-\)]\s*/, '').trim(),
      estrategias_actividades: '',
      metas_medicion: ''
    }));
  }
  
  if (Array.isArray(objetivos)) {
    if (objetivos.length === 0) {
      return [{
        id: `obj_${Date.now()}_1`,
        texto: '',
        estrategias_actividades: '',
        metas_medicion: ''
      }];
    }
    
    return objetivos.map((obj, idx) => {
      if (typeof obj === 'string') {
        return {
          id: `obj_string_${Date.now()}_${idx}`,
          texto: obj,
          estrategias_actividades: '',
          metas_medicion: ''
        };
      }
      
      return {
        id: obj.id || `obj_${Date.now()}_${idx}`,
        texto: obj.texto || obj.objetivo || '',
        estrategias_actividades: obj.estrategias_actividades || '',
        metas_medicion: obj.metas_medicion || ''
      };
    });
  }
  
  return [{
    id: `obj_${Date.now()}_1`,
    texto: '',
    estrategias_actividades: '',
    metas_medicion: ''
  }];
};

const TablaPlanTerapeutico = ({ campo, value = [], onChange, disabled = false }) => {
  const { configuracion } = campo;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // ========== TODOS LOS HOOKS PRIMERO ==========
  const [areas, setAreas] = useState(() => {
    return prepararDatosParaCargar(value);
  });

  const [areasExpandidas, setAreasExpandidas] = useState({});

  // Efecto para cuando cambia el value prop
  useEffect(() => {
    if (value && Array.isArray(value) && value.length > 0) {
      const areasNormalizadas = prepararDatosParaCargar(value);
      if (areas.length === 0 || areasNormalizadas.length !== areas.length) {
        setAreas(areasNormalizadas);
      }
    }
  }, [value]);

  useEffect(() => {
    // Actualizar números automáticamente
    const areasNumeradas = areas.map((area, index) => ({
      ...area,
      numero: index + 1
    }));
    
    const numeracionCambio = areasNumeradas.some((area, index) => 
      area.numero !== areas[index]?.numero
    );
    
    if (numeracionCambio) {
      setAreas(areasNumeradas);
    }
  }, [areas.length]);

  // ========== FUNCIONES AUXILIARES ==========
  function crearAreaVacia(indice = 0) {
    return {
      numero: indice + 1,
      area: '',
      objetivos_generales: '',
      objetivos_especificos: [
        {
          id: `obj_${Date.now()}_1`,
          texto: '',
          estrategias_actividades: '',
          metas_medicion: ''
        }
      ]
    };
  }

  const handleCellChange = (rowIndex, field, newValue) => {
    const nuevasAreas = [...areas];
    nuevasAreas[rowIndex][field] = newValue;
    setAreas(nuevasAreas);
    onChange(prepararDatosParaGuardar(nuevasAreas));
  };

  const handleObjetivoChange = (areaIndex, objetivoIndex, field, newValue) => {
    const nuevasAreas = [...areas];
    nuevasAreas[areaIndex].objetivos_especificos[objetivoIndex][field] = newValue;
    setAreas(nuevasAreas);
    onChange(prepararDatosParaGuardar(nuevasAreas));
  };

  const agregarArea = () => {
    if (configuracion.max_filas && areas.length >= configuracion.max_filas) {
      return;
    }
    const nuevaArea = crearAreaVacia(areas.length);
    const nuevasAreas = [...areas, nuevaArea];
    setAreas(nuevasAreas);
    onChange(prepararDatosParaGuardar(nuevasAreas));
  };

  const eliminarArea = (index) => {
    if (areas.length <= (configuracion.min_filas || 1)) {
      return;
    }
    const nuevasAreas = areas.filter((_, i) => i !== index);
    // Renumerar
    const areasRenumeradas = nuevasAreas.map((area, idx) => ({
      ...area,
      numero: idx + 1
    }));
    setAreas(areasRenumeradas);
    onChange(prepararDatosParaGuardar(areasRenumeradas));
  };

  const agregarObjetivo = (areaIndex) => {
    const nuevasAreas = [...areas];
    const nuevoObjetivo = {
      id: `obj_${Date.now()}_${nuevasAreas[areaIndex].objetivos_especificos.length + 1}`,
      texto: '',
      estrategias_actividades: '',
      metas_medicion: ''
    };
    nuevasAreas[areaIndex].objetivos_especificos.push(nuevoObjetivo);
    setAreas(nuevasAreas);
    onChange(nuevasAreas);
  };

  const eliminarObjetivo = (areaIndex, objetivoIndex) => {
    const nuevasAreas = [...areas];
    if (nuevasAreas[areaIndex].objetivos_especificos.length > 1) {
      nuevasAreas[areaIndex].objetivos_especificos.splice(objetivoIndex, 1);
      setAreas(nuevasAreas);
      onChange(prepararDatosParaGuardar(nuevasAreas));
    }
  };

  const toggleAreaExpanded = (areaIndex) => {
    setAreasExpandidas(prev => ({
      ...prev,
      [areaIndex]: !prev[areaIndex]
    }));
  };

  const contarObjetivosEspecificos = (area) => {
    return area.objetivos_especificos ? area.objetivos_especificos.length : 0;
  };

  const contarTotalObjetivos = () => {
    return areas.reduce((total, area) => total + contarObjetivosEspecificos(area), 0);
  };

  // ========== CONDICIONES Y RETURNS TEMPRANOS ==========
  // Vista de solo lectura REMOVIDA - Siempre editable para psicoterapia
  // if (disabled && value && value.length > 0) {
  //   return vista de solo lectura...
  // }

  // ========== RETURN PRINCIPAL ==========
  return (
    <Box sx={{ width: '100%' }}>
      <TableContainer component={Paper} variant="outlined">
        <Table sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell width="5%">N°</TableCell>
              <TableCell width={isMobile ? "25%" : "17%"}>
                ÁREAS PARA INTERVENIR
                {configuracion.columnas?.find(c => c.id === 'area')?.requerido && 
                  <Typography component="span" color="error"> *</Typography>
                }
              </TableCell>
              <TableCell width={isMobile ? "35%" : "18%"}>
                OBJETIVOS GENERALES
                {configuracion.columnas?.find(c => c.id === 'objetivos_generales')?.requerido && 
                  <Typography component="span" color="error"> *</Typography>
                }
              </TableCell>
              <TableCell width={isMobile ? "40%" : "55%"}>
                OBJETIVOS ESPECÍFICOS Y PLAN DE ACCIÓN
                <Typography variant="caption" display="block" color="textSecondary">
                  (Objetivos, Estrategias y Metas)
                </Typography>
              </TableCell>
              {/* !disabled && configuracion.permitir_eliminar && */ configuracion.permitir_eliminar && (
                <TableCell width="5%" align="center">
                  Acción
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {areas.map((area, areaIndex) => {
              const isExpanded = areasExpandidas[areaIndex] !== false; // Por defecto expandido
              
              return (
                <TableRow key={areaIndex} sx={{ verticalAlign: 'top', '& > td': { borderBottom: 'none' } }}>
                  <TableCell>
                    <Typography variant="body2" align="center">
                      {String(area.numero).padStart(2, '0')}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <TextField
                      fullWidth
                      multiline
                      rows={6}
                      variant="outlined"
                      size="small"
                      placeholder="Nombre del área"
                      value={area.area || ''}
                      onChange={(e) => handleCellChange(areaIndex, 'area', e.target.value)}
                      required={configuracion.columnas?.find(c => c.id === 'area')?.requerido}
                      // disabled={disabled} // REMOVIDO: Siempre editable
                      sx={{ 
                        '& .MuiInputBase-root': {
                          minHeight: '120px',
                          alignItems: 'flex-start'
                        }
                      }}
                    />
                  </TableCell>
                  
                  <TableCell>
                    <CampoModal
                      value={area.objetivos_generales || ''}
                      onChange={(newValue) => handleCellChange(areaIndex, 'objetivos_generales', newValue)}
                      label={`Objetivos Generales - Área ${area.numero}`}
                      placeholder="Describa los objetivos generales del área..."
                      // disabled={disabled} // REMOVIDO: Siempre editable
                      required={configuracion.columnas?.find(c => c.id === 'objetivos_generales')?.requerido}
                      minHeight={400}
                      helperText="Define los objetivos principales que se buscan lograr en esta área de intervención"
                      sx={{ minHeight: '160px' }}
                    />
                  </TableCell>
                  
                  <TableCell>
                    <Box>
                      {/* Header de la sub-tabla */}
                      <Box 
                        display="flex" 
                        alignItems="center" 
                        justifyContent="space-between" 
                        mb={1}
                        sx={{ 
                          p: 1, 
                          bgcolor: 'grey.100', 
                          borderRadius: 1,
                          cursor: 'pointer'
                        }}
                        onClick={() => toggleAreaExpanded(areaIndex)}
                      >
                        <Stack direction="row" spacing={1} alignItems="center">
                          <IconButton size="small">
                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                          <Typography variant="body2" fontWeight="medium">
                            Objetivos Específicos ({contarObjetivosEspecificos(area)})
                          </Typography>
                        </Stack>
                        
                        {/* !disabled && */ (
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              agregarObjetivo(areaIndex);
                            }}
                            variant="text"
                          >
                            Agregar objetivo
                          </Button>
                        )}
                      </Box>
                      
                      <Collapse in={isExpanded}>
                        <Table size="small" sx={{ tableLayout: 'fixed' }}>
                          <TableHead>
                            <TableRow>
                              <TableCell width="5%" sx={{ padding: '8px' }}>#</TableCell>
                              <TableCell width="31.67%" sx={{ padding: '8px' }}>Objetivo Específico</TableCell>
                              <TableCell width="31.67%" sx={{ padding: '8px' }}>Estrategias y Actividades</TableCell>
                              <TableCell width="31.67%" sx={{ padding: '8px' }}>Metas (Medidas de medición)</TableCell>
                              {/* !disabled && */ <TableCell width="5%" sx={{ padding: '8px' }}>Acción</TableCell>}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {area.objetivos_especificos.map((objetivo, objIndex) => (
                              <TableRow key={objetivo.id || objIndex}>
                                <TableCell sx={{ padding: '8px' }}>
                                  <Typography variant="caption" color="textSecondary">
                                    {objIndex + 1}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ padding: '8px' }}>
                                  <CampoModal
                                    value={objetivo.texto || ''}
                                    onChange={(newValue) => handleObjetivoChange(areaIndex, objIndex, 'texto', newValue)}
                                    label={`Objetivo Específico ${objIndex + 1} - Área ${area.numero}`}
                                    placeholder={`Describa el objetivo específico ${objIndex + 1}...`}
                                    // disabled={disabled} // REMOVIDO: Siempre editable
                                    minHeight={250}
                                    helperText="Describa de manera específica y medible qué se quiere lograr"
                                    sx={{ minHeight: '100px', width: '100%' }}
                                  />
                                </TableCell>
                                <TableCell sx={{ padding: '8px' }}>
                                  <CampoModal
                                    value={objetivo.estrategias_actividades || ''}
                                    onChange={(newValue) => handleObjetivoChange(areaIndex, objIndex, 'estrategias_actividades', newValue)}
                                    label={`Estrategias y Actividades - Objetivo ${objIndex + 1}`}
                                    placeholder="Describa las estrategias y actividades específicas..."
                                    // disabled={disabled || !objetivo.texto} // REMOVIDO: Siempre editable
                                    minHeight={250}
                                    helperText="Detalle las metodologías, técnicas y actividades específicas a realizar"
                                    sx={{ minHeight: '100px', width: '100%' }}
                                  />
                                </TableCell>
                                <TableCell sx={{ padding: '8px' }}>
                                  <CampoModal
                                    value={objetivo.metas_medicion || ''}
                                    onChange={(newValue) => handleObjetivoChange(areaIndex, objIndex, 'metas_medicion', newValue)}
                                    label={`Metas e Indicadores - Objetivo ${objIndex + 1}`}
                                    placeholder="Defina las metas e indicadores de medición..."
                                    // disabled={disabled || !objetivo.texto} // REMOVIDO: Siempre editable
                                    minHeight={250}
                                    helperText="Establezca criterios medibles y temporales para evaluar el progreso"
                                    sx={{ minHeight: '100px', width: '100%' }}
                                  />
                                </TableCell>
                                {/* !disabled && */ (
                                  <TableCell sx={{ padding: '8px' }}>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => eliminarObjetivo(areaIndex, objIndex)}
                                      disabled={area.objetivos_especificos.length <= 1}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                )}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Collapse>
                    </Box>
                  </TableCell>
                  
                  {/* !disabled && */ configuracion.permitir_eliminar && (
                    <TableCell align="center">
                      <Tooltip title="Eliminar área">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => eliminarArea(areaIndex)}
                          disabled={areas.length <= (configuracion.min_filas || 1)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* !disabled && */ configuracion.permitir_agregar && (
        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={agregarArea}
            disabled={configuracion.max_filas && areas.length >= configuracion.max_filas}
            size="small"
          >
            {configuracion.texto_agregar || 'Agregar área'}
          </Button>
          
          {areas.length > 0 && (
            <Box component="span" sx={{ ml: 2 }}>
              <Chip 
                label={`${areas.length} área${areas.length > 1 ? 's' : ''} - ${contarTotalObjetivos()} objetivo${contarTotalObjetivos() !== 1 ? 's' : ''} específico${contarTotalObjetivos() !== 1 ? 's' : ''}`}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default TablaPlanTerapeutico;